/**
 * docsTemplates — starter content for typed Docs. PURE (no Firebase/TipTap import),
 * so the shapes are unit-testable and can be reused server-side later.
 *
 * WHY TEMPLATES RATHER THAN DOC TYPES: production paperwork is all the same
 * technical thing — a titled table — so each new document kind is a TEMPLATE, not
 * a new `type` with its own code path. Only `script` earns a distinct type,
 * because it needs a genuinely different schema (semantic prose nodes).
 *
 * Column sets are taken from standard theatre production paperwork, not invented:
 *   - Master cue sheet: cue number, page in script, cue type (line vs visual),
 *     description; departments SQ / LX / Rail / Set / Spot / FX.
 *   - Instrument schedule: organized BY STAGE POSITION — position, unit, type,
 *     wattage, color, focus, circuit, dimmer, template.
 *   - Channel hookup: the same data organized BY CHANNEL — that reorganization is
 *     the whole distinction between the two documents, so both ship.
 *   - Wireless mic assignment: character, actor, pack, frequency, body placement.
 *
 * NOTE ON SCOPE: a true light plot and mic plot are SCALE DRAFTED DRAWINGS (CAD,
 * ¼"=1'-0"). Docs deliberately does not attempt those — it owns the tabular
 * paperwork that accompanies them, which is what actually gets handed to crew.
 */

export const DOC_TYPES = {
	DOC: 'doc',
	SHOW_FLOW: 'show-flow',
	SCRIPT: 'script',
};

/* ---- Doc types as a navigable axis --------------------------------------
 *
 * `type` has been stored on every doc since v1 but nothing ever read it back,
 * so a script, a show flow and a planning page were indistinguishable in the
 * list. This is the display + filter side of that field.
 *
 * "Pages" rather than "Docs" for the plain type: inside a surface already called
 * Docs, a filter chip reading "Docs" says nothing. It also keeps clear of the
 * three-name rule in core.md — Song Notes · Lesson Recap · Docs — by not
 * inventing a fourth sense of an existing word.
 */
export const DOC_TYPE_META = {
	[DOC_TYPES.DOC]: { label: 'Pages', icon: 'fa-file-lines' },
	[DOC_TYPES.SHOW_FLOW]: { label: 'Show flows', icon: 'fa-list-ol' },
	[DOC_TYPES.SCRIPT]: { label: 'Scripts', icon: 'fa-masks-theater' },
};

/** A doc's type, with anything missing or unrecognised treated as a plain page. */
export function docTypeOf(doc) {
	const t = doc?.type;
	return DOC_TYPE_META[t] ? t : DOC_TYPES.DOC;
}

/** Icon for a doc's type — used per row so the kinds are tellable apart. */
export function docTypeIcon(doc) {
	return DOC_TYPE_META[docTypeOf(doc)].icon;
}

/**
 * How many docs of each type, in catalogue order.
 *
 * Drives which filters are OFFERED: a chip for a type nothing uses would filter
 * to an empty list, which reads as the feature being broken — the same reason
 * the "/" menu hides table actions outside a table.
 */
export function docTypeCounts(docs) {
	const counts = new Map(Object.keys(DOC_TYPE_META).map((t) => [t, 0]));
	(docs || []).forEach((d) => {
		const t = docTypeOf(d);
		counts.set(t, counts.get(t) + 1);
	});
	return [...counts.entries()]
		.filter(([, n]) => n > 0)
		.map(([type, count]) => ({ type, count, ...DOC_TYPE_META[type] }));
}

/** Docs of one type. A null/absent type means "all", unfiltered. */
export function filterDocsByType(docs, type) {
	if (!type) return docs || [];
	return (docs || []).filter((d) => docTypeOf(d) === type);
}

/* ---- TipTap JSON builders ---------------------------------------------- */

const text = (s) => ({ type: 'text', text: s });
const para = (s) => (s ? { type: 'paragraph', content: [text(s)] } : { type: 'paragraph' });
/* Empty content must be OMITTED, never expressed as an empty text node:
   ProseMirror forbids `{type:'text', text:''}` and silently REPAIRS the document
   by dropping it. That repair then gets autosaved, so a malformed template
   doesn't just render wrong — it overwrites itself in the database. */
const heading = (level, s) =>
	s ? { type: 'heading', attrs: { level }, content: [text(s)] } : { type: 'heading', attrs: { level } };
const headerCell = (s) => ({ type: 'tableHeader', content: [para(s)] });
const cell = (s) => ({ type: 'tableCell', content: [para(s)] });
const row = (cells) => ({ type: 'tableRow', content: cells });

/** A table with a header row plus `blankRows` empty rows ready to fill in. */
function table(columns, blankRows = 4) {
	const rows = [row(columns.map(headerCell))];
	for (let i = 0; i < blankRows; i += 1) {
		rows.push(row(columns.map(() => cell(''))));
	}
	return { type: 'table', content: rows };
}

/* ---- The templates ------------------------------------------------------ */

export const TEMPLATES = [
	{
		key: 'blank',
		label: 'Blank doc',
		hint: 'Start from nothing',
		icon: 'fa-file',
		docType: DOC_TYPES.DOC,
		build: () => ({
			type: 'doc',
			content: [heading(1, ''), para()],
		}),
	},
	{
		key: 'show-flow',
		label: 'Show flow',
		hint: 'Recital running order — song by song',
		icon: 'fa-list-ol',
		docType: DOC_TYPES.SHOW_FLOW,
		/* Columns specified by Anthony 2026-07-24. Song-centric rather than the
		   generic segment/department grid: a CYV recital IS a list of songs, and
		   who is singing, on which mic, entering from where is what crew needs.
		   Lighting is usually set with the venue tech at rehearsal, so the column
		   exists to carry a rough preset rather than a cue number. */
		build: () => ({
			type: 'doc',
			content: [
				heading(1, 'Show flow'),
				para('Venue · Date · Version 1'),
				table(
					[
						'#',
						'Song',
						'Singer(s)',
						'Mic assignment',
						'Lighting',
						'Entrance',
						'Exit',
						'Notes',
					],
					8
				),
				// Legends: these documents go to venue crew who don't know CYV's
				// shorthand, and stage-direction abbreviations are ambiguous to
				// anyone who isn't standing on the stage.
				para('Entrance / exit: SL = stage left · SR = stage right · US = upstage · DS = downstage · House = from the audience'),
				para('Lighting presets: Full wash · Warm wash · Solo special · Spot follow · Blackout · Hold from previous'),
			],
		}),
	},
	/* Script — the one entry that is a real doc TYPE rather than a table
	   template, because its schema genuinely differs: six semantic prose nodes,
	   not cells. The element model lives in services/scriptFormat.js.

	   The starter content is a correctly-formatted FRAGMENT on purpose. These
	   scripts are written for the Triple Threat class and are meant to double as
	   a teaching tool — students should see what a real script looks like — so
	   the first thing a writer opens is the format done right: scene heading,
	   stage direction, cue and speech, a parenthetical, a song cue naming the
	   number in caps, and a lyric stanza. */
	{
		key: 'script',
		label: 'Script',
		hint: 'Dramatists Guild musical format — Tab changes element',
		icon: 'fa-masks-theater',
		docType: DOC_TYPES.SCRIPT,
		build: () => ({
			type: 'doc',
			content: [
				heading(1, 'Script'),
				{ type: 'scriptSceneHeading', content: [text('ACT ONE, SCENE ONE')] },
				{
					type: 'scriptDirection',
					content: [text('The stage is bare. A single work light burns.')],
				},
				{ type: 'scriptCharacter', content: [text('MARGO')] },
				{ type: 'scriptDialogue', content: [text('We open in three weeks.')] },
				{ type: 'scriptCharacter', content: [text('EVE')] },
				{ type: 'scriptParenthetical', content: [text('quietly')] },
				{ type: 'scriptDialogue', content: [text('Then we had better begin.')] },
				// A song cue is a stage direction naming the number in caps — the
				// convention from the format spec, shown rather than described.
				{
					type: 'scriptDirection',
					content: [text('The music of BEGIN AGAIN begins. She sings.')],
				},
				{
					type: 'scriptLyric',
					content: [text('THERE IS A LIGHT THAT NEVER QUITE GOES OUT')],
				},
				{ type: 'scriptLyric', content: [text('IT WAITS BACKSTAGE FOR ME')] },
			],
		}),
	},
	{
		key: 'cue-sheet',
		label: 'Master cue sheet',
		hint: 'Every cue in the show, by department',
		icon: 'fa-clipboard-list',
		docType: DOC_TYPES.SHOW_FLOW,
		build: () => ({
			type: 'doc',
			content: [
				heading(1, 'Master cue sheet'),
				para('Version 1'),
				table(
					['Cue #', 'Page', 'Dept', 'Type (line/visual)', 'Description', 'Called on'],
					6
				),
				para('Departments: SQ (sound) · LX (light) · Rail · Set · Spot · FX'),
			],
		}),
	},
	{
		key: 'instrument-schedule',
		label: 'Instrument schedule',
		hint: 'Lighting paperwork, organized by stage position',
		icon: 'fa-lightbulb',
		docType: DOC_TYPES.SHOW_FLOW,
		build: () => ({
			type: 'doc',
			content: [
				heading(1, 'Instrument schedule'),
				para('Organized by hanging position. Accompanies the light plot.'),
				table(
					[
						'Position',
						'Unit #',
						'Type',
						'Watt',
						'Color',
						'Focus',
						'Circuit',
						'Dimmer',
						'Template',
					],
					6
				),
			],
		}),
	},
	{
		key: 'channel-hookup',
		label: 'Channel hookup',
		hint: 'The same rig, organized by channel',
		icon: 'fa-sliders',
		docType: DOC_TYPES.SHOW_FLOW,
		build: () => ({
			type: 'doc',
			content: [
				heading(1, 'Channel hookup'),
				para('Organized by channel — the counterpart to the instrument schedule.'),
				table(
					['Channel', 'Dimmer', 'Position', 'Unit #', 'Type', 'Purpose', 'Color'],
					6
				),
			],
		}),
	},
	{
		key: 'mic-plot',
		label: 'Mic assignment',
		hint: 'Wireless channels — who wears what',
		icon: 'fa-microphone-lines',
		docType: DOC_TYPES.SHOW_FLOW,
		build: () => ({
			type: 'doc',
			content: [
				heading(1, 'Mic assignment'),
				para('Wireless channel assignments. Version 1'),
				table(
					[
						'Mic #',
						'Performer',
						'Character',
						'Pack',
						'Frequency',
						'Placement',
						'Scenes',
						'Notes',
					],
					6
				),
			],
		}),
	},
];

const BY_KEY = Object.fromEntries(TEMPLATES.map((t) => [t.key, t]));

export function getTemplate(key) {
	return BY_KEY[key] || BY_KEY.blank;
}

/** Starter content + the doc type that template implies. */
export function buildFromTemplate(key) {
	const t = getTemplate(key);
	return { content: t.build(), type: t.docType };
}

/** Column headers of the first table in a doc — used by tests and previews. */
export function tableHeadersOf(content) {
	const tbl = (content?.content || []).find((n) => n.type === 'table');
	if (!tbl) return [];
	const first = tbl.content?.[0];
	if (!first) return [];
	return first.content
		.filter((c) => c.type === 'tableHeader')
		.map((c) => c.content?.[0]?.content?.[0]?.text || '');
}
